/* ═══════════════════════════════════════════════════════════════════════════
   Sons de placeholder SYNTHÉTISÉS.

   ⚠️ Ce sont des sons provisoires. Le plan prévoit Sonniss GameAudioGDC
   (royalty-free, sans attribution, à vie) comme base, complété par Kenney Audio
   en CC0 — mais ces banques demandent un tri, un montage, et un travail de
   sound design qui n'est pas du code.

   Les synthétiser ici plutôt que d'attendre a deux vertus :

     · le jeu a un retour sonore MAINTENANT, ce qui est ce qui compte pour
       juger le game feel — un tir sans son ne « pèse » rien
     · toute la machinerie (bus, limiteur, vol de voix, ducking, déblocage
       iOS) est éprouvée avec de vrais tampons, donc prête à recevoir des
       échantillons réels sans rien changer d'autre que ce fichier

   Chaque son est rendu UNE FOIS au démarrage dans un AudioBuffer, exactement
   comme le serait un échantillon décodé. Le reste du système ne fait aucune
   différence entre les deux.

   Contrainte respectée dès maintenant : tout est MONO. Le plan est explicite —
   `decodeAudioData` produit du PCM Float32 non compressé, 384 Ko/s en stéréo
   contre 192 en mono. Sur soixante effets, le choix se voit.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface SynthSpec {
  readonly id: string;
  readonly durationSec: number;
  readonly render: (t: number, dur: number, rnd: () => number) => number;
}

/** Enveloppe percussive : attaque quasi instantanée, extinction exponentielle.
 *  C'est la forme de presque tout ce qui se passe dans un shmup. */
function env(t: number, dur: number, attack = 0.004, power = 3): number {
  if (t < attack) return t / attack;
  const k = (t - attack) / (dur - attack);
  return Math.pow(1 - k, power);
}

const TAU = Math.PI * 2;

/** Générateur pseudo-aléatoire local : les sons doivent être identiques d'un
 *  lancement à l'autre, sinon deux joueurs n'entendent pas le même jeu. */
function seeded(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let x = s;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

export const SPECS: readonly SynthSpec[] = [
  {
    // Tir joueur : très court, timbre clair, descente rapide. Il se joue dix
    // fois par seconde — il doit être présent sans jamais fatiguer.
    id: 'player_shot',
    durationSec: 0.09,
    render: (t, dur) => {
      const f = 900 - 500 * (t / dur);
      return Math.sin(TAU * f * t) * env(t, dur, 0.002, 4) * 0.25;
    },
  },
  {
    // Tir ennemi : plus grave et plus mat, pour ne jamais être confondu avec
    // celui du joueur. La distinction sonore double la distinction visuelle.
    id: 'enemy_shot',
    durationSec: 0.12,
    render: (t, dur, rnd) => {
      const f = 320 - 120 * (t / dur);
      const noise = (rnd() * 2 - 1) * 0.15;
      return (Math.sin(TAU * f * t) * 0.7 + noise) * env(t, dur, 0.003, 3) * 0.22;
    },
  },
  {
    // Tir LASER ennemi : grave, menaçant, un peu bourdonnant — jamais aigu.
    id: 'enemy_laser',
    durationSec: 0.26,
    render: (t, dur) => {
      const f = 280 - 80 * (t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 1.5 * t) * 0.35) *
        (0.85 + 0.15 * Math.sin(TAU * 60 * t)) *
        env(t, dur, 0.004, 2.5) * 0.22;
    },
  },
  {
    // Tir en SPIRALE / à tête chercheuse : un gazouillis inquiétant qui monte.
    id: 'enemy_spiral',
    durationSec: 0.2,
    render: (t, dur) => {
      const f = 300 + 220 * (t / dur);
      return Math.sin(TAU * f * t) * (0.7 + 0.3 * Math.sin(TAU * 44 * t)) * env(t, dur, 0.003, 3) * 0.18;
    },
  },
  {
    // SALVE (anneau/mur/rafale) : un « fwoom » dense, grave, avec du souffle.
    id: 'enemy_burst',
    durationSec: 0.24,
    render: (t, dur, rnd) => {
      const noise = (rnd() * 2 - 1) * 0.4;
      const body = Math.sin(TAU * (200 - 100 * (t / dur)) * t);
      return (noise + body * 0.7) * env(t, dur, 0.003, 2.5) * 0.24;
    },
  },
  {
    id: 'explode_small',
    durationSec: 0.28,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const body = Math.sin(TAU * (180 - 120 * (t / dur)) * t);
      return (noise * 0.6 + body * 0.4) * env(t, dur, 0.002, 2.5) * 0.32;
    },
  },
  {
    id: 'explode_med',
    durationSec: 0.55,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const body = Math.sin(TAU * (120 - 80 * (t / dur)) * t);
      return (noise * 0.55 + body * 0.45) * env(t, dur, 0.003, 2) * 0.42;
    },
  },
  {
    // Dégât joueur : l'information la plus critique du jeu. Timbre descendant
    // et dissonant, volontairement distinct de tout le reste — on doit le
    // reconnaître même au milieu d'un rideau.
    id: 'player_hit',
    durationSec: 0.7,
    render: (t, dur, rnd) => {
      const f = 420 * Math.pow(0.35, t / dur);
      const noise = (rnd() * 2 - 1) * 0.25;
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 1.48 * t) * 0.5 + noise) *
        env(t, dur, 0.001, 1.8) * 0.3;
    },
  },
  {
    // Death-bomb réussie : montant, brillant, gratifiant. C'est le moment le
    // plus satisfaisant du jeu et il doit s'entendre comme tel.
    id: 'death_bomb',
    durationSec: 0.6,
    render: (t, dur) => {
      const f = 300 + 900 * (t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 2 * t) * 0.4) * env(t, dur, 0.002, 1.5) * 0.3;
    },
  },
  {
    id: 'bomb',
    durationSec: 0.9,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const sweep = Math.sin(TAU * (900 - 780 * (t / dur)) * t);
      return (noise * 0.4 + sweep * 0.6) * env(t, dur, 0.004, 1.6) * 0.45;
    },
  },
  {
    // Frôlement : très discret. Il récompense sans distraire — un graze trop
    // sonore pousserait à frôler pour le son plutôt que pour le score.
    id: 'graze',
    durationSec: 0.06,
    render: (t, dur) => Math.sin(TAU * 2200 * t) * env(t, dur, 0.001, 5) * 0.09,
  },
  {
    id: 'pickup',
    durationSec: 0.13,
    render: (t, dur) => {
      const f = 660 + 440 * (t / dur);
      return Math.sin(TAU * f * t) * env(t, dur, 0.002, 3) * 0.16;
    },
  },
  {
    // POWER-UP ramassé : un carillon ASCENDANT à trois notes (arpège majeur), plus
    // riche et gratifiant que le blip de monnaie — c'est une récompense qu'on est
    // allé chercher sous le feu, elle doit s'entendre.
    id: 'powerup',
    durationSec: 0.34,
    render: (t, dur) => {
      const k = t / dur;
      // Trois paliers de hauteur (do–mi–sol montant), chacun sur son tiers.
      const step = k < 0.33 ? 0 : k < 0.66 ? 1 : 2;
      const f = 523 * (step === 0 ? 1 : step === 1 ? 1.26 : 1.5);
      const tone = Math.sin(TAU * f * t) + Math.sin(TAU * f * 2 * t) * 0.3;
      return tone * env(t, dur, 0.004, 2.2) * 0.16;
    },
  },
  {
    // Télégraphe d'apparition : bref, neutre, non alarmant. Il annonce, il
    // n'effraie pas.
    id: 'warn',
    durationSec: 0.18,
    render: (t, dur) => Math.sin(TAU * 520 * t) * env(t, dur, 0.006, 2) * 0.12,
  },
  {
    // Arme SECONDAIRE : plus grave et plus charnue que le tir primaire, pour
    // qu'un clic droit ne se confonde jamais avec le tir automatique.
    id: 'secondary',
    durationSec: 0.16,
    render: (t, dur, rnd) => {
      const f = 480 - 260 * (t / dur);
      const noise = (rnd() * 2 - 1) * 0.1;
      return (Math.sin(TAU * f * t) * 0.8 + Math.sin(TAU * f * 1.5 * t) * 0.3 + noise) *
        env(t, dur, 0.002, 3) * 0.24;
    },
  },
  {
    // MORT du joueur (pas l'annulation death-bomb) : grave, descendant, sans
    // appel — la sanction, pas la récompense. À distinguer nettement du
    // `death_bomb` montant et brillant.
    id: 'player_death',
    durationSec: 0.9,
    render: (t, dur, rnd) => {
      const f = 260 * Math.pow(0.25, t / dur);
      const noise = (rnd() * 2 - 1) * 0.4 * (1 - t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 0.5 * t) * 0.6 + noise) *
        env(t, dur, 0.001, 1.4) * 0.4;
    },
  },
  {
    // RÉAPPARITION : bref souffle montant, la vie qui revient. Attaque douce
    // (0,02) pour un « surgissement » plutôt qu'une percussion.
    id: 'respawn',
    durationSec: 0.5,
    render: (t, dur) => {
      const f = 300 + 700 * (t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 2.01 * t) * 0.3) *
        env(t, dur, 0.02, 2) * 0.2;
    },
  },
  {
    // BOUCLIER qui encaisse : un tintement métallique bref (partiels non
    // harmoniques). Il dit « ça a été absorbé », rassurant, jamais alarmant.
    id: 'shield',
    durationSec: 0.22,
    render: (t, dur) =>
      (Math.sin(TAU * 1400 * t) + Math.sin(TAU * 2100 * t) * 0.5 + Math.sin(TAU * 3740 * t) * 0.3) *
      env(t, dur, 0.001, 3.5) * 0.15,
  },
  {
    // MONTÉE DE NIVEAU : trois notes montantes (do–mi–sol), franches et
    // gratifiantes. Une récompense se lit à l'oreille.
    id: 'levelup',
    durationSec: 0.5,
    render: (t, dur) => {
      const notes = [523, 659, 784];
      const seg = dur / 3;
      const i = Math.min(2, Math.floor(t / seg));
      const lt = t - i * seg;
      return Math.sin(TAU * notes[i]! * lt) * env(lt, seg, 0.004, 3) * 0.18;
    },
  },
  {
    // FRANCHISSEMENT DE MONDE : une fanfare courte — accord qui gonfle plus un
    // scintillement montant. Le seul moment « on a passé un cap » du jeu.
    id: 'world_clear',
    durationSec: 1.1,
    render: (t, dur) => {
      const base = 392;
      const chord =
        Math.sin(TAU * base * t) +
        Math.sin(TAU * base * 1.26 * t) * 0.7 +
        Math.sin(TAU * base * 1.5 * t) * 0.7 +
        Math.sin(TAU * base * 2 * t) * 0.4;
      const shimmer = Math.sin(TAU * (base * 3 + 200 * (t / dur)) * t) * 0.2;
      return (chord * 0.22 + shimmer) * env(t, dur, 0.02, 1.3) * 0.3;
    },
  },
  {
    // BOSS qui APPARAÎT : une montée grave et menaçante — un grondement qui enfle
    // vers un impact sourd. Le seul son du jeu qui dit « ça devient sérieux ».
    id: 'boss_appear',
    durationSec: 1.2,
    render: (t, dur, rnd) => {
      const k = t / dur;
      const rumble = Math.sin(TAU * (55 + 45 * k) * t) + Math.sin(TAU * (82 + 30 * k) * t) * 0.6;
      const air = (rnd() * 2 - 1) * 0.15 * k;
      return (rumble * 0.4 + air) * env(t, dur, 0.05, 0.9) * 0.32;
    },
  },
  {
    // BOSS qui change de PHASE : une alerte BRÈVE et montante — deux notes qui
    // grimpent d'un cran, un « il change » qui claque sans masquer l'action.
    id: 'boss_phase',
    durationSec: 0.34,
    render: (t, dur) => {
      const k = t / dur;
      const f = k < 0.5 ? 330 : 494; // saut d'un cran vers le haut
      const tone = Math.sin(TAU * f * t) + Math.sin(TAU * f * 1.5 * t) * 0.4;
      return tone * env(t, dur, 0.004, 2.4) * 0.2;
    },
  },
  {
    // BOSS ABATTU : une grande déflagration qui descend, doublée d'un swell — la
    // récompense du combat, distincte de l'explosion générique d'un ennemi.
    id: 'boss_death',
    durationSec: 1.4,
    render: (t, dur, rnd) => {
      const k = t / dur;
      const boom = Math.sin(TAU * (150 - 110 * k) * t);
      const noise = (rnd() * 2 - 1) * (1 - k) * 0.5;
      const swell = Math.sin(TAU * (196 + 160 * k) * t) * 0.3 * (1 - k);
      return (boom * 0.5 + noise * 0.4 + swell) * env(t, dur, 0.01, 1.1) * 0.34;
    },
  },
  {
    // OBSTACLE brisé : un craquement sec et grave, mat — de la roche, pas du
    // métal. Noyé de bruit, corps bas.
    id: 'rock',
    durationSec: 0.3,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const body = Math.sin(TAU * (140 - 90 * (t / dur)) * t);
      return (noise * 0.7 + body * 0.3) * env(t, dur, 0.001, 2.8) * 0.3;
    },
  },

  /* ── ULTIME ──────────────────────────────────────────────────────────────── */
  {
    // CHARGE de l'ulti : une montée en puissance qui accélère et enfle — le
    // « trou noir » qui aspire l'énergie. Amplitude qui CROÎT (pas d'extinction),
    // calée sur la durée de charge.
    id: 'ulti_charge',
    durationSec: 0.9,
    render: (t, dur) => {
      const k = t / dur;
      const f = 90 + 520 * k * k; // hauteur qui accélère
      const shimmer = Math.sin(TAU * f * 4 * t) * 0.15 * k;
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 1.5 * t) * 0.4 + shimmer) * (k * k) * 0.2;
    },
  },
  {
    // DÉFLAGRATION : la décharge. Boom grave profond + souffle + libération
    // d'énergie brillante. Fort — c'est le grand moment.
    id: 'ulti_blast',
    durationSec: 0.85,
    render: (t, dur, rnd) => {
      const k = t / dur;
      const noise = (rnd() * 2 - 1) * (1 - k);
      const boom = Math.sin(TAU * (80 * Math.pow(0.4, k)) * t);
      const energy = Math.sin(TAU * (300 + 200 * k) * t) * 0.3 * (1 - k);
      return (noise * 0.4 + boom * 0.7 + energy) * env(t, dur, 0.001, 1.6) * 0.5;
    },
  },
  {
    // Ulti MISSILE : une nuée qui part — souffle massif + grondement.
    id: 'ulti_missile',
    durationSec: 0.6,
    render: (t, dur, rnd) => {
      const noise = (rnd() * 2 - 1) * 0.5;
      const sweep = Math.sin(TAU * (500 - 350 * (t / dur)) * t);
      const rumble = Math.sin(TAU * 90 * t) * 0.4;
      return (noise * 0.5 + sweep * 0.5 + rumble) * env(t, dur, 0.002, 1.8) * 0.42;
    },
  },
  {
    // Ulti MINE : un champ qui saute — gros boom grave et mat.
    id: 'ulti_mine',
    durationSec: 0.6,
    render: (t, dur, rnd) => {
      const noise = (rnd() * 2 - 1) * 0.4;
      const boom = Math.sin(TAU * (110 * Math.pow(0.5, t / dur)) * t);
      return (noise * 0.4 + boom * 0.7) * env(t, dur, 0.002, 1.7) * 0.44;
    },
  },
  {
    // Ulti BEAM (le canon orbital) : un faisceau énorme et soutenu.
    id: 'ulti_beam',
    durationSec: 0.75,
    render: (t, dur) => {
      const k = t / dur;
      const core = Math.sin(TAU * (180 + 60 * Math.sin(TAU * 5 * t)) * t);
      const air = Math.sin(TAU * 340 * t) * 0.4;
      const swell = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) * 0.6;
      return (core + air) * Math.max(0, swell) * 0.4;
    },
  },
  {
    // Ulti ÉLECTRIQUE : orage — crépitement dense + boom.
    id: 'ulti_electric',
    durationSec: 0.6,
    render: (t, dur, rnd) => {
      const crackle = (rnd() * 2 - 1) * (rnd() < 0.6 ? 1 : 0.3);
      const boom = Math.sin(TAU * (120 - 60 * (t / dur)) * t) * 0.5;
      return (crackle * 0.6 + boom) * env(t, dur, 0.001, 1.9) * 0.4;
    },
  },
  {
    // Ulti DÉPLOIEMENT : une nuée orbitale — clac + vrombissement massif.
    id: 'ulti_deploy',
    durationSec: 0.6,
    render: (t, dur, rnd) => {
      const clack = t < 0.04 ? (rnd() * 2 - 1) : 0;
      const whir = Math.sin(TAU * (200 + 100 * (t / dur)) * t) * (0.6 + 0.4 * Math.sin(TAU * 30 * t));
      return (clack * 0.5 + whir * 0.6) * env(t, dur, 0.001, 1.8) * 0.4;
    },
  },
  {
    // Ulti RIPOSTE : une déflagration rapprochée brève et puissante.
    id: 'ulti_riposte',
    durationSec: 0.5,
    render: (t, dur, rnd) => {
      const noise = (rnd() * 2 - 1) * 0.4;
      const body = Math.sin(TAU * (260 - 160 * (t / dur)) * t);
      return (noise * 0.4 + body * 0.7) * env(t, dur, 0.001, 2) * 0.42;
    },
  },
  {
    // IMPACT sur un GROS ennemi : un « toc » charnu et mat, plus lourd que le
    // tir. Court (il se joue à la cadence du tir), grave.
    id: 'hit_heavy',
    durationSec: 0.12,
    render: (t, dur, rnd) => {
      const noise = (rnd() * 2 - 1) * 0.4;
      const body = Math.sin(TAU * (200 - 120 * (t / dur)) * t);
      return (noise * 0.4 + body * 0.6) * env(t, dur, 0.001, 4) * 0.24;
    },
  },
  {
    // KAMIKAZE : le rugissement du piqué — un whoosh grave et menaçant qui fond.
    id: 'enemy_dive',
    durationSec: 0.4,
    render: (t, dur, rnd) => {
      const f = 180 - 100 * (t / dur);
      const air = (rnd() * 2 - 1) * 0.3 * (t / dur);
      return (Math.sin(TAU * f * t) + air) * env(t, dur, 0.02, 1.6) * 0.28;
    },
  },
  {
    // SURCHAUFFE : le canon crache — un grésillement de vapeur (bruit filtré qui
    // s'éteint) doublé d'une alarme GRAVE à deux temps. Volontairement chaud, sans
    // aigu perçant : c'est une alerte à ressentir, pas une fourchette sur du métal.
    id: 'overheat',
    durationSec: 0.45,
    render: (t, dur, rnd) => {
      const k = t / dur;
      // Grésillement : bruit qui décroît vite, comme de l'eau sur une plaque.
      const sizzle = (rnd() * 2 - 1) * (1 - k) * 0.5;
      // Alarme grave à deux temps (bat à ~7 Hz), fondamentale qui glisse vers le bas.
      const beat = 0.6 + 0.4 * Math.sin(TAU * 7 * t);
      const alarm = Math.sin(TAU * (150 - 40 * k) * t) * beat * 0.5;
      return (sizzle + alarm) * env(t, dur, 0.01, 1.4) * 0.3;
    },
  },
  {
    // ENRAYAGE : le canon se BLOQUE — un CLAC métallique sec en tête, suivi d'un
    // gros SIFFLEMENT de vapeur qui s'échappe (bruit filtré long). Le son de
    // « ça s'arrête », plus dur que l'alarme de surchauffe.
    id: 'jam',
    durationSec: 0.6,
    render: (t, dur, rnd) => {
      const k = t / dur;
      // Clac : une impulsion grave très brève au tout début.
      const clackEnv = t < 0.05 ? 1 - t / 0.05 : 0;
      const clack = Math.sin(TAU * 90 * t) * clackEnv;
      // Sifflement : bruit qui décroît lentement, comme un jet de vapeur sous pression.
      const hiss = (rnd() * 2 - 1) * (1 - k) * (1 - k) * 0.6;
      return (clack * 0.7 + hiss) * env(t, dur, 0.001, 1.2) * 0.32;
    },
  },

  /* ── Tir PRIMAIRE, un timbre par canon ─────────────────────────────────── */
  {
    // PLASMA : grave, gras, un « vwoom » lourd.
    id: 'shot_plasma',
    durationSec: 0.15,
    render: (t, dur, rnd) => {
      const f = 300 - 150 * (t / dur);
      return (Math.sin(TAU * f * t) * 0.8 + (rnd() * 2 - 1) * 0.12) * env(t, dur, 0.002, 3) * 0.26;
    },
  },
  {
    // FAISCEAU : zap brillant et net.
    id: 'shot_beam',
    durationSec: 0.12,
    render: (t, dur) =>
      (Math.sin(TAU * 1500 * t) + Math.sin(TAU * 2250 * t) * 0.4) * env(t, dur, 0.001, 4) * 0.2,
  },
  {
    // TRAQUEUR : un « chwiip » qui monte puis retombe — le missile qui accroche.
    id: 'shot_homing',
    durationSec: 0.14,
    render: (t, dur) => {
      const k = t / dur;
      const f = 500 + 700 * Math.sin(Math.PI * k);
      return Math.sin(TAU * f * t) * env(t, dur, 0.002, 3) * 0.22;
    },
  },
  {
    // PERFORANT : fin, sec, aigu — une aiguille.
    id: 'shot_pierce',
    durationSec: 0.09,
    render: (t, dur) => Math.sin(TAU * (1300 - 300 * (t / dur)) * t) * env(t, dur, 0.001, 5) * 0.2,
  },
  {
    // RICOCHET : un « bong » ressort, légèrement métallique.
    id: 'shot_bounce',
    durationSec: 0.13,
    render: (t, dur) => {
      const f = 700 * Math.pow(0.6, t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 1.5 * t) * 0.4) * env(t, dur, 0.002, 4) * 0.2;
    },
  },
  {
    // ÉVENTAIL : un « pshhh » plus large et soufflé.
    id: 'shot_spread',
    durationSec: 0.12,
    render: (t, dur, rnd) => {
      const f = 800 - 300 * (t / dur);
      return (Math.sin(TAU * f * t) * 0.5 + (rnd() * 2 - 1) * 0.5) * env(t, dur, 0.001, 4) * 0.2;
    },
  },

  /* ── DÉCLENCHEMENT des secondaires, par archétype ───────────────────────── */
  {
    // Salve/missile : thunk mécanique puis départ.
    id: 'sec_missile',
    durationSec: 0.22,
    render: (t, dur, rnd) => {
      const f = 420 - 240 * (t / dur);
      return (Math.sin(TAU * f * t) * 0.7 + (rnd() * 2 - 1) * 0.2) * env(t, dur, 0.002, 3) * 0.26;
    },
  },
  {
    // Mine larguée : « plop » mat et court.
    id: 'sec_mine',
    durationSec: 0.16,
    render: (t, dur) => Math.sin(TAU * (220 - 120 * (t / dur)) * t) * env(t, dur, 0.003, 3.5) * 0.22,
  },
  {
    // Rayon amorcé : un zap qui monte.
    id: 'sec_beam',
    durationSec: 0.2,
    render: (t, dur) => {
      const f = 400 + 900 * (t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 2 * t) * 0.3) * env(t, dur, 0.004, 2) * 0.2;
    },
  },
  {
    // Orage électrique : crépitement.
    id: 'sec_electric',
    durationSec: 0.22,
    render: (t, dur, rnd) => {
      const crackle = (rnd() * 2 - 1) * (rnd() < 0.5 ? 1 : 0.3);
      return (crackle * 0.7 + Math.sin(TAU * 1400 * t) * 0.3) * env(t, dur, 0.001, 2.5) * 0.22;
    },
  },
  {
    // Déploiement (scies/drones) : clac mécanique + montée du bourdon.
    id: 'sec_deploy',
    durationSec: 0.2,
    render: (t, dur, rnd) => {
      const clack = t < 0.03 ? (rnd() * 2 - 1) : 0;
      const hum = Math.sin(TAU * (180 + 120 * (t / dur)) * t);
      return (clack * 0.6 + hum * 0.5) * env(t, dur, 0.001, 2.5) * 0.22;
    },
  },
  {
    // Riposte rapprochée : un « shing » sec et vif.
    id: 'sec_riposte',
    durationSec: 0.14,
    render: (t, dur) =>
      (Math.sin(TAU * 1800 * t) + Math.sin(TAU * 2700 * t) * 0.5) * env(t, dur, 0.001, 4.5) * 0.2,
  },

  /* ── BOUCLES en vol ─────────────────────────────────────────────────────────
     ⚠️ Amplitude CONSTANTE (aucune enveloppe) et fréquences MULTIPLES de 1/durée
     (= cycles entiers sur la boucle), donc raccord sans clic. Durée 0,5 s →
     toute fréquence PAIRE boucle proprement (f × 0,5 est entier). */
  {
    // Missile en vol : un vrai souffle de RÉACTEUR — grondement grave, combustion
    // qui palpite, et un chuintement aigu de gaz. Toutes les fréquences sont
    // PAIRES (cycles entiers sur 0,5 s) donc la boucle se referme sans clic.
    id: 'missile_loop',
    durationSec: 0.5,
    render: (t) => {
      const rumble = Math.sin(TAU * 84 * t) * 0.4 + Math.sin(TAU * 126 * t) * 0.22;
      // Combustion : un médium turbulé par une modulation d'amplitude rapide.
      const burn = Math.sin(TAU * 336 * t) * (0.55 + 0.45 * Math.sin(TAU * 38 * t));
      // Chuintement : quatre aigus proches qui battent entre eux → un « ssshh »
      // sans vrai bruit (donc bouclable).
      const hiss =
        (Math.sin(TAU * 1604 * t) +
          Math.sin(TAU * 1808 * t) +
          Math.sin(TAU * 2012 * t) +
          Math.sin(TAU * 2216 * t)) *
        0.16;
      const wobble = 0.85 + 0.15 * Math.sin(TAU * 4 * t);
      return (rumble * 0.5 + burn * 0.32 + hiss) * wobble * 0.12;
    },
  },
  {
    // Rayon continu : un bourdon soutenu et brillant.
    id: 'beam_loop',
    durationSec: 0.5,
    render: (t) =>
      (Math.sin(TAU * 300 * t) + Math.sin(TAU * 450 * t) * 0.5 + Math.sin(TAU * 902 * t) * 0.2) *
      (0.9 + 0.1 * Math.sin(TAU * 6 * t)) *
      0.09,
  },
  {
    // Scies qui tournent : un vrombissement modulé (la rotation).
    id: 'saw_loop',
    durationSec: 0.5,
    render: (t) =>
      (Math.sin(TAU * 200 * t) + Math.sin(TAU * 400 * t) * 0.5 + Math.sin(TAU * 600 * t) * 0.3) *
      (0.5 + 0.5 * Math.sin(TAU * 24 * t)) *
      0.1,
  },

  /* ── Variété ─────────────────────────────────────────────────────────────── */
  {
    // IMPACT d'un tir sur un obstacle (sans le casser) : un « tok » sec et bref,
    // roche + un peu de métal. Court et discret : il se joue à la cadence du tir.
    id: 'impact',
    durationSec: 0.1,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const tone = Math.sin(TAU * (600 - 300 * (t / dur)) * t);
      return (noise * 0.5 + tone * 0.5) * env(t, dur, 0.001, 5) * 0.2;
    },
  },
  {
    // Gros astéroïde : plus grave, plus long et plus « lourd » que `rock`.
    id: 'rock_big',
    durationSec: 0.45,
    render: (t, dur, rnd) => {
      const noise = rnd() * 2 - 1;
      const body = Math.sin(TAU * (95 - 60 * (t / dur)) * t);
      return (noise * 0.6 + body * 0.4) * env(t, dur, 0.001, 2.4) * 0.34;
    },
  },

  /* ── Interface ──────────────────────────────────────────────────────────
     Volontairement DISCRETS et courts : un menu doit répondre, jamais claironner.
     Timbres propres et synthétiques, sans bruit, pour trancher avec les sons de
     jeu (percussifs et sales). */
  {
    // Navigation : le curseur passe d'un élément à l'autre. Un tic très bref.
    id: 'ui_move',
    durationSec: 0.04,
    render: (t, dur) => Math.sin(TAU * 1800 * t) * env(t, dur, 0.001, 4) * 0.08,
  },
  {
    // Sélection neutre : un clic net et court.
    id: 'ui_select',
    durationSec: 0.06,
    render: (t, dur) => Math.sin(TAU * 920 * t) * env(t, dur, 0.001, 4) * 0.12,
  },
  {
    // Validation d'une action positive (JOUER, LANCER…) : deux tons MONTANTS.
    id: 'ui_confirm',
    durationSec: 0.14,
    render: (t, dur) => {
      const f = 660 + 500 * (t / dur);
      return (Math.sin(TAU * f * t) + Math.sin(TAU * f * 2 * t) * 0.3) * env(t, dur, 0.002, 3) * 0.13;
    },
  },
  {
    // Retour / annulation : deux tons DESCENDANTS, le miroir de `ui_confirm`.
    id: 'ui_back',
    durationSec: 0.12,
    render: (t, dur) => {
      const f = 720 - 320 * (t / dur);
      return Math.sin(TAU * f * t) * env(t, dur, 0.002, 3) * 0.12;
    },
  },
  {
    // Bascule d'un réglage (curseur, liste) : un tic neutre, distinct du move.
    id: 'ui_toggle',
    durationSec: 0.05,
    render: (t, dur) => Math.sin(TAU * 1200 * t) * env(t, dur, 0.001, 4) * 0.1,
  },
  {
    // Action REFUSÉE (nœud verrouillé, achat impossible) : un buzz grave et mat.
    // Le battement de deux fréquences proches donne le grain « erreur ».
    id: 'ui_denied',
    durationSec: 0.16,
    render: (t, dur) =>
      (Math.sin(TAU * 160 * t) + Math.sin(TAU * 166 * t)) * env(t, dur, 0.002, 2) * 0.13,
  },
  {
    // ACHAT au hangar : gratifiant, deux notes brillantes qui montent plus un
    // scintillement — le seul son « dépense récompensée » du jeu.
    id: 'ui_purchase',
    durationSec: 0.34,
    render: (t, dur) => {
      const seg = dur * 0.5;
      const f = t < seg ? 784 : 1046; // sol → do
      const lt = t < seg ? t : t - seg;
      const shimmer = Math.sin(TAU * 2600 * t) * 0.12 * (1 - t / dur);
      return (Math.sin(TAU * f * lt) * env(lt, seg, 0.002, 3) + shimmer) * 0.14;
    },
  },
];

/** Rend tous les sons dans des AudioBuffer mono. Appelé une fois au démarrage,
 *  pendant l'écran de chargement — jamais en cours de partie. */
export function renderAll(ctx: BaseAudioContext): Map<string, AudioBuffer> {
  const out = new Map<string, AudioBuffer>();
  const rate = ctx.sampleRate;

  for (const spec of SPECS) {
    const n = Math.max(1, Math.floor(spec.durationSec * rate));
    const buf = ctx.createBuffer(1, n, rate);
    const data = buf.getChannelData(0);
    const rnd = seeded(spec.id.length * 7919 + n);

    for (let i = 0; i < n; i++) {
      const t = i / rate;
      let v = spec.render(t, spec.durationSec, rnd);
      if (v > 1) v = 1;
      else if (v < -1) v = -1;
      data[i] = v;
    }
    out.set(spec.id, buf);
  }
  return out;
}

/** Durée déclarée d'un son, en millisecondes — le pool de voix en a besoin
 *  pour savoir quand libérer un slot, sans interroger le graphe audio. */
export function durationMs(id: string): number {
  const s = SPECS.find((x) => x.id === id);
  return s ? s.durationSec * 1000 : 200;
}
